import { Alert, Box, Button, ExpandableSection, SpaceBetween, Tabs } from '@amzn/awsui-components-react';
import TPTaxReportTableGrid from 'src/components/reports/TPTaxReportTableGrid';
import React from 'react';
import { ReportMetaData, TPTaxReportData, TPTaxReportMapResponse } from 'src/models/reports/TPTaxReport';
import ServiceCollection from 'src/services/ServiceCollection';
import CONSTANTS, {
    isPliTabKey,
    IS_TESTED_PARTY_REPORT_ENABLED,
    LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID,
} from 'src/utils/constants';
import { useHistory } from 'react-router-dom';
import { MANAGE_EXCEPTIONS_TAB_URL_PREFIX } from 'src/components/ic-account-mapping/constants';
import {
    MultiCLIPassthroughCostSection,
    MultiCLIPLIReconciliationSection,
} from 'src/components/reports/PassthroughCostSections';
import { buildCliHeaderLabels, formatCliWithCurrency, buildCliCurrencyMap } from 'src/components/reports/TPMultiCLIReportsShared';
import DrillDownDimensionSelector from 'src/components/reports/lefReport/DrillDownDimensionSelector';
import {
    dimensionUnavailableMessage,
    filterZeroSegmentRows,
    getDrillDownDimension,
    isAccountDimension,
    isSegmentRow,
    resolveDimensionView,
    SEGMENT_DESCRIPTION_UNAVAILABLE,
    splitSegmentDisplayName,
} from 'src/components/reports/segmentDrillDown';

/** Filters a CLI-keyed map to only include CLIs present in the given visible-CLI list. */
function filterCliMap<T>(cliMap: { [cli: string]: T } | null | undefined, visibleHeaders: string[] | undefined): { [cli: string]: T } | null | undefined {
    if (!cliMap || !visibleHeaders || visibleHeaders.length === 0) return cliMap;
    const visibleCLIs = new Set(visibleHeaders);
    const filtered: { [cli: string]: T } = {};
    for (const cli of Object.keys(cliMap)) {
        if (visibleCLIs.has(cli)) {
            filtered[cli] = cliMap[cli];
        }
    }
    return Object.keys(filtered).length > 0 ? filtered : null;
}

export default function TPMultiCLIReportTableGrid(props: {
    reportData: TPTaxReportMapResponse;
    services: ServiceCollection;
    fetchReportLoading: boolean;
    reportMetaData?: ReportMetaData;
    isPassthroughDrilldownEnabled?: boolean;
    // Gated behind the reportCurrencyOverlay AppConfig flag. When false, CLI headers/tab labels
    // and the per-CLI currency line render without FRAMES currency (pre-feature behaviour).
    showCliCurrency?: boolean;
    // LEF segment drill-down (lefSegmentDrillDownEnabled). Off/absent for CWB, which passes none of
    // these and therefore keeps rendering reportContent.TP_PNL with no control bar.
    lefSegmentDrillDownEnabled?: boolean;
    // Selection state is owned by the report view so it outlives Functional Segment and page changes.
    selectedDrillDownDimensionId?: string;
    onDrillDownDimensionChange?: (dimensionId: string) => void;
    showZeroValueSegments?: boolean;
    onShowZeroValueSegmentsChange?: (showZeroValueSegments: boolean) => void;
}) {
    const history = useHistory();

    // Drill-down resolution. With the flag off this pins to Account, whose reportContentKey IS
    // TP_PNL and whose filters are all no-ops, so the section renders exactly as it did before.
    const drillDownEnabled = props.lefSegmentDrillDownEnabled ?? false;
    const dimension = getDrillDownDimension(
        drillDownEnabled ? props.selectedDrillDownDimensionId : LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID
    );
    const showZeroValueSegments = props.showZeroValueSegments ?? false;
    const { available: dimensionAvailable, rows: dimensionRows } = resolveDimensionView(
        props.reportData?.reportContent,
        dimension
    );
    const tpPnlRows = showZeroValueSegments ? dimensionRows : filterZeroSegmentRows(dimensionRows, dimension);

    // Story 1.3: split a segment's `"<code>  <description>"` displayName so the code reads as a code.
    // Section and account rows fall through to the payload's displayName untouched, so only the
    // dimension hierarchy changes appearance — and only in a non-Account view.
    const renderSegmentAttributeName = (item: TPTaxReportData): React.ReactNode => {
        if (!isSegmentRow(item, dimension)) {
            return item.displayName;
        }
        const { code, description } = splitSegmentDisplayName(item.displayName);
        if (!code) {
            return item.displayName;
        }
        return (
            <span>
                <Box variant="strong" display="inline">
                    {code}
                </Box>
                {`  ${description ?? SEGMENT_DESCRIPTION_UNAVAILABLE}`}
            </span>
        );
    };

    // CLI column headers/tab labels in the LEF & CWB (multi-CLI) views are annotated with the
    // FRAMES currency sourced from the DETAIL section, e.g. "<cli>" -> "<cli> (USD)".
    // Only when the currency-display feature flag is on; otherwise everything falls back to raw CLIs.
    const showCliCurrency = props.showCliCurrency ?? false;
    const cliHeaderLabels = showCliCurrency
        ? buildCliHeaderLabels(props.reportData, props.reportData?.reportHeaders)
        : undefined;
    const cliCurrencyMap = showCliCurrency ? buildCliCurrencyMap(props.reportData) : {};

    return (
        <SpaceBetween size={'xl'}>
            <ExpandableSection variant="container" headerText="Details" disableContentPaddings={true}>
                <TPTaxReportTableGrid
                    columnHeaders={props.reportData?.reportHeaders}
                    columnHeaderLabels={cliHeaderLabels}
                    data={props.reportData?.reportContent?.DETAIL}
                    services={props.services}
                    loadingStatus={props.fetchReportLoading}
                />
            </ExpandableSection>
            <ExpandableSection
                variant="container"
                headerText={IS_TESTED_PARTY_REPORT_ENABLED ? 'Tested Party TP P&L' : 'TP Profit and Loss (P&L)'}
                disableContentPaddings={true}
                headerActions={
                    props.reportMetaData?.reportName === CONSTANTS.REPORT_TYPES.TP_TAX_CWB_REPORT &&
                    !!props.reportMetaData?.workbookLabel ? (
                        <Button
                            onClick={() => {
                                history.push(
                                    `${MANAGE_EXCEPTIONS_TAB_URL_PREFIX}?workbookLabel=${encodeURIComponent(props.reportMetaData?.workbookLabel as string)}`
                                );
                            }}>
                            {' '}
                            Manage P&L Mapping{' '}
                        </Button>
                    ) : null
                }>
                {drillDownEnabled && (
                    <DrillDownDimensionSelector
                        selectedDimensionId={dimension.id}
                        onDimensionChange={(dimensionId) => props.onDrillDownDimensionChange?.(dimensionId)}
                        showZeroValueSegments={showZeroValueSegments}
                        onShowZeroValueSegmentsChange={(checked) => props.onShowZeroValueSegmentsChange?.(checked)}
                        disabled={props.fetchReportLoading}
                    />
                )}
                {/* Story 1.6a: a dimension the report has no rows for gets an explicit message. Never
                    an empty or all-zero grid, which would read as real data. */}
                {!dimensionAvailable ? (
                    <Box padding={{ horizontal: 'l', bottom: 'l' }}>
                        <Alert type="info" header={`${dimension.label} breakdown unavailable`}>
                            {dimensionUnavailableMessage(dimension)}
                        </Alert>
                    </Box>
                ) : (
                    <TPTaxReportTableGrid
                        columnHeaders={props.reportData?.reportHeaders}
                        columnHeaderLabels={cliHeaderLabels}
                        data={tpPnlRows}
                        services={props.services}
                        loadingStatus={props.fetchReportLoading}
                        renderAttributeName={isAccountDimension(dimension) ? undefined : renderSegmentAttributeName}
                        stickyHeader={drillDownEnabled}
                    />
                )}
            </ExpandableSection>
            <ExpandableSection
                variant="container"
                headerText={IS_TESTED_PARTY_REPORT_ENABLED ? 'Tested Party PLI' : 'Profit Level Indicator (PLI)'}
                disableContentPaddings={true}
                defaultExpanded={true}>
                <Tabs
                    tabs={Object.entries(props.reportData?.reportContent || {})
                        .filter(([key]) => isPliTabKey(key))
                        .map(([key, value]) => ({
                            label: formatCliWithCurrency(key, cliCurrencyMap),
                            id: key,
                            content: (
                                <TPTaxReportTableGrid
                                    data={value}
                                    services={props.services}
                                    columnHeaders={props.reportData?.reportHeaders}
                                    columnHeaderLabels={cliHeaderLabels}
                                    loadingStatus={props.fetchReportLoading}
                                />
                            ),
                        }))}
                />
            </ExpandableSection>
            {props.isPassthroughDrilldownEnabled && (
                <>
                    <MultiCLIPassthroughCostSection
                        cliPassthroughDetails={filterCliMap(props.reportData?.cliPassthroughDetails, props.reportData?.filteredHeaders ?? props.reportData?.reportHeaders)}
                        serviceCollection={props.services}
                        cliCurrencyMap={cliCurrencyMap}
                    />
                    <MultiCLIPLIReconciliationSection
                        cliPliReconciliation={filterCliMap(props.reportData?.cliPliReconciliation, props.reportData?.filteredHeaders ?? props.reportData?.reportHeaders)}
                        serviceCollection={props.services}
                        cliCurrencyMap={cliCurrencyMap}
                    />
                </>
            )}
        </SpaceBetween>
    );
}
